import Box from "@/common/components/Box";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { CompanyGovernmentAllocationDistribution, ControlledByType } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import { BoxesIcon, BriefcaseBusinessIcon, ChevronDownIcon, ChevronUpIcon, FileBoxIcon } from "lucide-react";
import GovernmentAllocationDistributionProductCategoriesModal from "./GovernmentAllocationDistribution/UpdateDistributionModal";
import { formatCurrency, formatDecimalUnit, formatDecimalWithGroupedUnit } from "@/common/utils/formatters";
import ResumeItem from "@/common/components/ResumeItem";
import Button from "@/common/components/Button";
import { useState } from "react";
import { GovernmentAllocationDistributionDeleteModal } from "@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationDistribution/DeleteDistributionModal";
import { GovernmentAllocationProductCategoryDistributionDeleteModal } from "@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationDistribution/DeleteProductCategoryDistributionModal";
import { GovernmentAllocationProductDistributionDeleteModal } from "@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationDistribution/DeleteProductDistributionModal";
import DropdownMenu from "@/common/components/DropdownMenu";
import { BranchGovernmentAllocationDistributionsResetModal } from "./GovernmentAllocationDistribution/ResetBranchDistributionsModal";
import { useParams } from "react-router-dom";

interface GovernmentAllocationDistributionBranchItemProps {
  distribution: CompanyGovernmentAllocationDistribution;
  controlledBy?: ControlledByType;
}

const GovernmentAllocationDistributionBranchItem = ({ distribution, controlledBy = 'currency' }: GovernmentAllocationDistributionBranchItemProps) => {
  const { id } = useParams()

  const [showProductCategoriesDistributions, setShowProductCategoriesDistributions] = useState(distribution.product_categories_distributions.length > 0);
  const [updateModalIsOpen, setUpdateModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [resetModalIsOpen, setResetModalIsOpen] = useState(false);

  const isQuantity = controlledBy === 'quantity';

  return (
    <Box className="flex flex-col gap-6 p-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ImageWithPlaceholder
            placeholderIcon={BriefcaseBusinessIcon}
            className="size-8"
            iconClassName="size-5"
            url={distribution.branch?.general_information?.company_logo?.url}
          />

          <p>{distribution.branch?.display_name}</p>
        </div>
        <DropdownMenu
          triggerClassName="bg-primary-blue hover:bg-primary-blue/80"
          triggerLabel="Ações"
          menuPositionAlign="end"
          options={[
            {
              label: 'Editar',
              onClick: () => setUpdateModalIsOpen(true),
              operationKlassToCheckPermission: "Admin::Billing::GovernmentAllocationDistributions::Update"
            },
            {
              label: 'Zerar distribuição',
              onClick: () => setResetModalIsOpen(true),
              operationKlassToCheckPermission: "Admin::Billing::GovernmentAllocations::ResetBranchRemainingDistributions"
            },
            {
              label: 'Deletar',
              onClick: () => setDeleteModalIsOpen(true),
              operationKlassToCheckPermission: "Admin::Billing::GovernmentAllocationDistributions::Stale"
            }
          ]}
        />
      </div>

      <div className="flex items-end justify-between">
        <div className="flex items-end gap-4">
          <ResumeItem
            label={isQuantity ? "Quantidade limite" : "Limite total"}
            value={isQuantity
              ? formatDecimalUnit(Number(distribution.total_quantity))
              : formatCurrency(Number(distribution.total_value))}
            valueClassName="text-md"
          />

          <ResumeItem
            label={isQuantity ? "Quantidade consumida" : "Total consumido"}
            className="text-yellow-400"
            value={isQuantity
              ? formatDecimalUnit(Number(distribution.consumed_quantity))
              : formatCurrency(Number(distribution.consumed_total))}
            valueClassName="text-md"
          />

          <ResumeItem
            label={isQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
            className="text-green-400"
            value={isQuantity
              ? formatDecimalUnit(Number(distribution.remaining_quantity))
              : formatCurrency(Number(distribution.remaining_total))}
            valueClassName="text-md"
          />
        </div>

        {distribution.product_categories_distributions.length > 0 && (
          <Button
            variant="outline-error"
            className="italic font-light text-sm"
            onClick={() => setShowProductCategoriesDistributions(!showProductCategoriesDistributions)}
          >
            {showProductCategoriesDistributions ? 'Esconder serviços e produtos' : 'Mostrar serviços e produtos'}

            {showProductCategoriesDistributions ? <ChevronUpIcon strokeWidth={1.5} className="size-4" /> : <ChevronDownIcon strokeWidth={1.5} className="size-4" />}
          </Button>
        )}
      </div>

      {showProductCategoriesDistributions && (
        <div className="flex flex-col gap-4">
          {distribution.product_categories_distributions.map((productCategoryDistribution) => (
            <Box className="flex flex-col gap-3" key={productCategoryDistribution.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BoxesIcon strokeWidth={1} />

                  <p>{productCategoryDistribution.product_category.display_name}</p>
                </div>

                <GovernmentAllocationProductCategoryDistributionDeleteModal productCategoryDistributionId={productCategoryDistribution.id} />
              </div>

              <div className="flex items-end gap-4 mx-6">
                <ResumeItem
                  label={isQuantity ? "Quantidade limite" : "Valor limite"}
                  value={isQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.total_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.total_value))
                  }
                  valueClassName="text-md"
                />

                <ResumeItem
                  label={isQuantity ? "Quantidade consumida" : "Total consumido"}
                  value={isQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.consumed_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.consumed_total))
                  }
                  valueClassName="text-md"
                  className="text-yellow-400"
                />

                <ResumeItem
                  label={isQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
                  value={isQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.remaining_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.remaining_total))
                  }
                  valueClassName="text-md"
                  className="text-green-400"
                />
              </div>

              {productCategoryDistribution.products_distributions.map((productDistribution) => (
                <Box className="p-3" key={productDistribution.id}>
                  <div className="flex flex-col gap-3 border-s border-blue-800 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileBoxIcon strokeWidth={1.5} />

                        <p>{productDistribution.product.display_name}</p>
                      </div>

                      <GovernmentAllocationProductDistributionDeleteModal productDistributionId={productDistribution.id} />
                    </div>

                    <div className="flex items-end gap-4">
                      <ResumeItem
                        label={isQuantity ? "Quantidade limite" : "Valor limite"}
                        value={isQuantity
                          ? formatDecimalUnit(Number(productDistribution.total_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.total_value))
                        }
                        valueClassName="text-md"
                      />

                      <ResumeItem
                        label={isQuantity ? "Quantidade consumida" : "Total consumido"}
                        value={isQuantity
                          ? formatDecimalUnit(Number(productDistribution.consumed_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.consumed_total))
                        }
                        valueClassName="text-md"
                        className="text-yellow-300"
                      />

                      <ResumeItem
                        label={isQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
                        value={isQuantity
                          ? formatDecimalUnit(Number(productDistribution.remaining_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.remaining_total))
                        }
                        valueClassName="text-md"
                        className="text-green-300"
                      />
                    </div>
                  </div>
                </Box>
              ))}
            </Box>
          ))}
        </div>
      )}
      <GovernmentAllocationDistributionDeleteModal
        isOpen={deleteModalIsOpen}
        setIsOpen={setDeleteModalIsOpen}
        distributionId={distribution.id}
      />

      <GovernmentAllocationDistributionProductCategoriesModal
        isOpen={updateModalIsOpen}
        setIsOpen={setUpdateModalIsOpen}
        distribution={distribution}
        controlledBy={controlledBy}
      />

      <BranchGovernmentAllocationDistributionsResetModal
        isOpen={resetModalIsOpen}
        setIsOpen={setResetModalIsOpen}
        governmentAllocationId={id}
        branchId={distribution.branch.id}
      />
    </Box>
  );
};

export default GovernmentAllocationDistributionBranchItem;
